// CampaignFrequencyControl.jsx — BespokeBridge
// Sets max messages per contact per time interval for a given campaign type.

import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../utils/firebase';

const CAMPAIGN_TYPE_NAMES = {
  email: 'Email',
  whatsapp: 'WhatsApp',
  push: 'Push Notification',
};

function CampaignFrequencyControl({ orgId, campaignType }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const [settings, setSettings] = useState({ maxFrequency: 10, interval: 'week' });

  const displayName = CAMPAIGN_TYPE_NAMES[campaignType] || campaignType;

  useEffect(() => {
    if (!orgId) return;
    const load = async () => {
      try {
        setLoading(true);
        const snap = await getDoc(doc(db, 'organizations', orgId));
        if (snap.exists()) {
          const s = snap.data().campaignFrequency?.[campaignType];
          if (s) setSettings({ maxFrequency: s.maxFrequency || 10, interval: s.interval || 'week' });
        }
      } catch (err) {
        console.error('Error loading frequency settings:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [orgId, campaignType]);

  const handleSave = async () => {
    if (!orgId) { setError('Organisation ID is required'); return; }
    if (settings.maxFrequency < 1 || settings.maxFrequency > 100) {
      setError('Maximum frequency must be between 1 and 100');
      setTimeout(() => setError(''), 3000);
      return;
    }
    try {
      setLoading(true);
      setError('');
      const ref = doc(db, 'organizations', orgId);
      const snap = await getDoc(ref);
      const current = snap.exists() ? (snap.data().campaignFrequency || {}) : {};
      await setDoc(ref, {
        campaignFrequency: {
          ...current,
          [campaignType]: {
            maxFrequency: parseInt(settings.maxFrequency),
            interval: settings.interval,
            updatedAt: new Date().toISOString(),
          },
        },
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setSuccess(`${displayName} frequency settings saved successfully!`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving frequency settings:', err);
      setError(`Failed to save settings: ${err.message}`);
      setTimeout(() => setError(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const intervalLabel = settings.interval === 'day' ? '24-hour' : settings.interval === 'week' ? '7-day' : '30-day';

  return (
    <div className="bb-comm-section-card">
      <div className="bb-comm-card-title">{displayName} Campaign Frequency</div>
      <p style={{ color: '#6B7280', fontSize: 13, marginBottom: 18 }}>
        Set the maximum number of {displayName.toLowerCase()} messages a contact can receive within a
        specified time period to prevent message fatigue and improve engagement.
      </p>

      {error   && <div className="bb-alert bb-alert--error"   style={{ marginBottom: 12 }}>{error}</div>}
      {success && <div className="bb-alert bb-alert--success" style={{ marginBottom: 12 }}>{success}</div>}

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 }}>
        <div className="bb-form-group" style={{ margin: 0, minWidth: 180 }}>
          <label className="bb-form-label">
            Maximum {displayName.toLowerCase()}s per contact
          </label>
          <input
            type="number"
            className="bb-form-input"
            min="1"
            max="100"
            value={settings.maxFrequency}
            onChange={e => {
              const v = parseInt(e.target.value);
              if (!isNaN(v) && v >= 1 && v <= 100) setSettings(s => ({ ...s, maxFrequency: v }));
            }}
            disabled={loading}
          />
        </div>

        <div className="bb-form-group" style={{ margin: 0, minWidth: 140 }}>
          <label className="bb-form-label">Within</label>
          <select
            className="bb-form-select"
            value={settings.interval}
            onChange={e => setSettings(s => ({ ...s, interval: e.target.value }))}
            disabled={loading}
          >
            <option value="day">Day</option>
            <option value="week">Week</option>
            <option value="month">Month</option>
          </select>
        </div>

        <button
          type="button"
          className="bb-btn-primary"
          onClick={handleSave}
          disabled={loading}
          style={{ marginBottom: 1 }}
        >
          {loading ? 'Saving…' : 'Save Frequency Settings'}
        </button>
      </div>

      <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#374151' }}>
        <strong>Example:</strong> Setting "{settings.maxFrequency} {displayName.toLowerCase()}s per {settings.interval}" means
        each contact will receive no more than {settings.maxFrequency} {displayName.toLowerCase()} messages within any {intervalLabel} period.
      </div>
      <div style={{ marginTop: 8, fontSize: 12, color: '#9CA3AF' }}>
        <strong>Note:</strong> This limit applies only to promotional campaigns sent to contacts.
      </div>
    </div>
  );
}

export default CampaignFrequencyControl;
